import React from "react";
import styled from "styled-components";

/**
 * The three-testimonial grid. Shared by the v2 and v3 landers: identical
 * layout, different kicker, heading and quotes. See Hero.styles.ts for the
 * px-not-rem note.
 */
const Wrapper = styled.section`
  position: relative;
  overflow: hidden;
  background: var(--brand-bone);

  .inner {
    margin: 0 auto;
    max-width: 1152px;
    padding: 80px 20px;
  }

  .head {
    margin-bottom: 48px;
    max-width: 672px;
  }

  .eyebrow {
    margin: 0 0 12px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.2em;
    color: var(--brand-red);
  }

  .heading {
    margin: 0;
    font-family: var(--brand-display);
    font-size: 36px;
    font-weight: 800;
    letter-spacing: -0.025em;
    line-height: 1.1;
    color: var(--brand-ink);
  }

  .cards {
    display: grid;
    gap: 24px;
  }

  @media screen and (min-width: 640px) {
    .inner {
      padding: 80px 32px;
    }
    .heading {
      font-size: 48px;
    }
    .cards {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  @media screen and (min-width: 1024px) {
    .inner {
      padding: 80px 32px 112px;
    }
  }
`;

const Card = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 24px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  background: #fff;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.25s;

  &:hover {
    box-shadow:
      0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -2px rgba(0, 0, 0, 0.1);
  }

  .media {
    position: relative;
    overflow: hidden;
    aspect-ratio: 4 / 3;
  }

  @supports not (aspect-ratio: 4 / 3) {
    .media {
      height: 0;
      padding-bottom: 75%;

      img {
        position: absolute;
        inset: 0;
      }
    }
  }

  .media img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.5s;
  }

  &:hover .media img {
    transform: scale(1.03);
  }

  .tag {
    position: absolute;
    left: 16px;
    top: 16px;
    border-radius: 999px;
    padding: 4px 10px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #fff;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2);
  }

  .body {
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: 24px;
  }

  .quote-mark {
    width: 24px;
    height: 24px;
    color: rgba(209, 31, 61, 0.3);
  }

  .quote {
    margin: 8px 0 0;
    font-family: var(--brand-display);
    font-size: 18px;
    font-weight: 600;
    line-height: 1.375;
    color: var(--brand-ink);
    flex-grow: 1;
  }

  .attribution {
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid rgba(0, 0, 0, 0.05);
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 14px;
  }

  .name {
    font-weight: 600;
    color: var(--brand-ink);
  }

  .place {
    color: rgba(14, 17, 22, 0.5);
  }
`;

const QuoteMark = () => (
  <svg
    className="quote-mark"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M7 7h4v4H8c0 3 2 4 3 4v2c-3 0-6-2-6-6V7Zm9 0h4v4h-3c0 3 2 4 3 4v2c-3 0-6-2-6-6V7Z" />
  </svg>
);

export type Story = {
  quote: string;
  name: string;
  place: string;
  tag: string;
  /** brand token for the tag chip, e.g. var(--brand-red) */
  color: string;
  image: string;
  alt: string;
};

const Stories = ({
  stories,
  eyebrow,
  heading,
  className = "section-lp2-stories",
}: {
  stories: Story[];
  eyebrow: string;
  heading: string;
  className?: string;
}) => {
  return (
    <Wrapper className={className}>
      <div className="inner">
        <div className="head">
          <p className="eyebrow">{eyebrow}</p>
          <h2 className="heading">{heading}</h2>
        </div>

        <div className="cards">
          {stories.map(({ quote, name, place, tag, color, image, alt }) => (
            <Card className="lp2-story" key={name}>
              <div className="media">
                <img src={image} alt={alt} loading="lazy" />
                <span className="tag" style={{ background: color }}>
                  {tag}
                </span>
              </div>
              <div className="body">
                <QuoteMark />
                <p className="quote">{quote}</p>
                <div className="attribution">
                  <span className="name">{name}</span>
                  <span className="place">{place}</span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </Wrapper>
  );
};

export default Stories;
